import { useMemo } from 'react';
import { Trash2, Copy, Eraser, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { listEffectDefinitionsByCategory } from '@/vfx/registry';
import { TimelineLaneMinimizeButton } from '@/components/videoEditor/TimelineLaneMinimizeButton';
import { timelineLaneClipAreaStyle, timelineLaneRowSizeStyle } from '@/lib/videoEditorTimelineLanes';
import { clipsInTimelineViewport } from '@/lib/timelineViewportClips';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const EFFECT_COLORS = {
  color_grade: '#8b5cf6',
  glow: '#f59e0b',
  motion_blur: '#3b82f6',
  lens_flare: '#ec4899',
  glitch: '#22c55e',
  sun_rays: '#fbbf24',
  rain: '#60a5fa',
  snow: '#e0f2fe',
  lens_drops: '#38bdf8',
  fog: '#94a3b8',
  fire_embers: '#f97316',
  dust_particles: '#d6d3d1',
  light_leaks: '#fb7185',
  lightning: '#fef08a',
  cinematic_clouds: '#cbd5e1',
  film_grain: '#a8a29e',
  vignette: '#57534e',
  chromatic_aberration: '#c084fc',
  anamorphic_streak: '#fbbf24',
  sparkles: '#fde68a',
  dream_haze: '#fda4af',
  heat_distortion: '#fb923c',
  vhs: '#4ade80',
};

const CATEGORY_LABELS = {
  color: 'Color',
  light: 'Light',
  motion: 'Motion',
  stylize: 'Stylize',
  environmental: 'Environmental',
  weather: 'Weather',
  lens: 'Lens',
  particles: 'Particles',
  cinematic: 'Cinematic',
};

function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * VFX timeline lane — draggable, resizable, copyable, removable clips.
 * Uses a button menu (not native <select>) so tooling can't inject invalid <span> children.
 */
export function EffectsTimelineTrack({
  track,
  trackIdx,
  pxPerSec,
  timelineActive,
  selectedClipId,
  draggingClipId,
  onToggleActive,
  onSelectClip,
  onBeginDrag,
  onBeginResize,
  onRemoveClip,
  onDuplicateClip,
  onAddEffect,
  onClearAll,
  setTrackRef,
  collapsed = false,
  onToggleCollapsed,
  viewportStartSec = 0,
  viewportEndSec = Number.POSITIVE_INFINITY,
}) {
  const grouped = useMemo(() => listEffectDefinitionsByCategory(), []);
  const clipCount = (track || []).length;
  const categories = useMemo(() => Object.entries(grouped), [grouped]);
  const visibleClips = useMemo(
    () => clipsInTimelineViewport(track, {
      startSec: viewportStartSec,
      endSec: viewportEndSec,
      forceIds: new Set([selectedClipId, draggingClipId].filter(Boolean)),
      getDuration: (clip) => Math.max(0.1, (Number(clip.endTime) || 0) - (Number(clip.startTime) || 0)),
    }),
    [track, viewportStartSec, viewportEndSec, selectedClipId, draggingClipId],
  );

  return (
    <div
      className="flex border-b"
      style={{
        borderColor: 'var(--glass-border)',
        opacity: timelineActive ? 1 : 0.45,
        ...timelineLaneRowSizeStyle(collapsed, 56),
      }}
      data-testid="vfx-timeline-track"
    >
      <div
        className={`w-36 shrink-0 sticky left-0 z-[100] flex flex-col justify-center gap-1 px-2 border-r shadow-[4px_0_10px_-2px_rgba(0,0,0,0.45)] ${collapsed ? 'py-0 overflow-hidden' : 'py-1.5'}`}
        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--glass-border)', zIndex: 100 }}
      >
        <div className="flex items-center justify-between gap-1 min-w-0">
          <div className="flex items-center gap-1 min-w-0">
            {onToggleCollapsed ? (
              <TimelineLaneMinimizeButton
                collapsed={collapsed}
                onToggle={onToggleCollapsed}
                label="VFX track"
                testId={`timeline-lane-minimize-vfx-${trackIdx}`}
              />
            ) : null}
            <TwinneeLogo className="w-3 h-3 shrink-0" style={{ color: '#a855f7' }} />
            <span className="text-[10px] font-medium truncate" style={{ color: 'var(--text-secondary)' }}>
              VFX
            </span>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {onClearAll && clipCount > 0 ? (
              <button
                type="button"
                title="Clear all VFX"
                aria-label="Clear all VFX"
                className="p-0.5 rounded hover:bg-white/10 disabled:opacity-40"
                onClick={onClearAll}
                data-testid="clear-all-vfx"
              >
                <Eraser className="w-3 h-3" style={{ color: 'var(--text-tertiary)' }} />
              </button>
            ) : null}
            <button
              type="button"
              onClick={onToggleActive}
              className="text-[10px] rounded px-1.5 py-0.5 border shrink-0"
              style={{
                borderColor: 'var(--glass-border)',
                color: timelineActive ? '#a855f7' : 'var(--text-tertiary)',
              }}
            >
              {timelineActive ? 'Active' : 'Off'}
            </button>
          </div>
        </div>
        {collapsed ? null : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="text-[9px] rounded border px-1 py-0.5 w-full flex items-center justify-between gap-1"
              style={{
                borderColor: 'var(--glass-border)',
                background: 'var(--bg-primary)',
                color: 'var(--text-secondary)',
              }}
              data-testid="add-vfx-effect-select"
            >
              <span className="truncate">Add VFX…</span>
              <ChevronDown className="w-3 h-3 shrink-0 opacity-70" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="max-h-80 w-56 overflow-y-auto"
            style={{ background: 'var(--bg-secondary)', borderColor: 'var(--glass-border)' }}
          >
            {categories.map(([cat, defs], catIdx) => (
              <div key={cat}>
                {catIdx > 0 ? <DropdownMenuSeparator /> : null}
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  {CATEGORY_LABELS[cat] || cat}
                </DropdownMenuLabel>
                {defs.map((d) => (
                  <DropdownMenuItem
                    key={d.type}
                    className="text-xs cursor-pointer"
                    onSelect={() => onAddEffect?.(d.type)}
                  >
                    {d.label}
                  </DropdownMenuItem>
                ))}
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        )}
      </div>

      <div
        className="relative flex-1 z-0"
        ref={(node) => setTrackRef(trackIdx, node)}
        style={{
          background: trackIdx % 2 === 0 ? 'var(--bg-primary)' : 'var(--bg-tertiary)',
          ...timelineLaneClipAreaStyle(collapsed),
        }}
      >
        {visibleClips.map((clip) => {
          const color = EFFECT_COLORS[clip.type] || '#a855f7';
          const duration = Math.max(0.1, clip.endTime - clip.startTime);
          const isSelected = selectedClipId === clip.id;
          return (
            <motion.div
              key={clip.id}
              className="absolute top-1 bottom-1 rounded-lg overflow-hidden flex items-center select-none group"
              style={{
                left: clip.startTime * pxPerSec,
                width: Math.max(32, duration * pxPerSec - 2),
                background: color,
                opacity: clip.enabled === false ? 0.4 : 0.88,
                cursor: draggingClipId === clip.id ? 'grabbing' : 'grab',
                boxShadow: isSelected ? '0 0 0 2px white, 0 0 0 4px #a855f7' : 'none',
                zIndex: isSelected || draggingClipId === clip.id ? 25 : 5,
              }}
              onMouseDown={(e) => onBeginDrag(e, clip, trackIdx)}
              onClick={() => onSelectClip(clip)}
              data-testid={`vfx-clip-${clip.id}`}
            >
              <div
                className="absolute left-0 top-0 bottom-0 w-3 z-20 cursor-ew-resize bg-white/10 opacity-0 group-hover:opacity-100"
                onMouseDown={(e) => onBeginResize(e, clip, trackIdx, 'left')}
              />
              <div
                className="absolute right-0 top-0 bottom-0 w-3 z-20 cursor-ew-resize bg-white/10 opacity-0 group-hover:opacity-100"
                onMouseDown={(e) => onBeginResize(e, clip, trackIdx, 'right')}
              />
              <div className="relative z-10 px-2 flex items-center justify-between w-full pointer-events-none">
                <span className="text-[10px] font-medium text-white truncate">
                  {clip.name || clip.type}
                </span>
                <div className="flex gap-0.5 shrink-0 ml-1 pointer-events-auto">
                  <button
                    type="button"
                    className="p-0.5 rounded hover:bg-white/20"
                    title="Duplicate"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); onDuplicateClip(clip); }}
                  >
                    <Copy className="w-2.5 h-2.5 text-white" />
                  </button>
                  <button
                    type="button"
                    className="p-0.5 rounded hover:bg-white/20"
                    title="Remove"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); onRemoveClip(clip.id); }}
                  >
                    <Trash2 className="w-2.5 h-2.5 text-white" />
                  </button>
                </div>
              </div>
              <span className="absolute bottom-0.5 left-2 text-[8px] text-white/70 pointer-events-none">
                {formatTime(duration)}
              </span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

export default EffectsTimelineTrack;
